import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { Icon } from 'ht-styleguide';

/* Components */
import SearchInput from 'src/components/Searchbar/SearchInput';

/* Utils */
import useOutsideClick from '@hooks/useOutsideClick';

/* Client Stores */
import { useResponsiveIsMobile, useResponsiveIsMobileOrTablet } from '@store/client/store.responsive';

/*  Constants */
import { DEFAULT_TEXTS } from 'src/components/Searchbar/constants';
import { SEARCH_ICON, SEARCH_WRAPPER_ID } from '@components/Searchbar/Hero/hero.constants';

// Styles
import styles from './hero.module.scss';
import { THEME_TYPES } from '@constants/theme';
import { isBrowser } from '@utils/ui';

type THeroSearch = {
  segmentLocation: string;
  theme?: (typeof THEME_TYPES)[keyof typeof THEME_TYPES];
};

type TInput = React.MouseEvent<HTMLDivElement, MouseEvent>;

const HeroSearchbar = ({ segmentLocation, theme }: THeroSearch) => {
  /* Client State */
  const isMobile = useResponsiveIsMobile();
  const isMobileOrTablet = useResponsiveIsMobileOrTablet();
  /* Local state/Refs */
  const [isActive, setisActive] = useState(false);
  const wrapperRef = useRef(null);

  /* Constants */
  let userAgent = '';
  if (isBrowser()) {
    ({ userAgent } = window.navigator);
  }
  const isIOS = userAgent.match(/iPad/i) || userAgent.match(/iPhone/i) || userAgent.match(/iPod/i);
  const showSearchStyles = styles.iosSearchContainer; // had to add a div and hide/show in css because ios was not autofocusing on the input first onclick.
  const searchInputClass = cn(styles.searchInput, theme);

  /* Methods */
  const handleClose = (e: TInput) => {
    setisActive(false);

    if (isActive) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleClick = (e: TInput) => {
    if (e && e.nativeEvent) e.nativeEvent.stopImmediatePropagation();

    setisActive(true);
    const element = document.getElementById(SEARCH_WRAPPER_ID);
    const yOffset = -72; // from top of results to top of input + 16px offset from top.
    const yPosition = element ? element.getBoundingClientRect().top + (window?.pageYOffset || 0) + yOffset : 0;
    if (!isIOS && isMobile) {
      window.scrollTo({
        top: yPosition,
        behavior: 'smooth',
      });
    }
  };

  const renderSearchComponent = () => {
    if (isMobileOrTablet && isIOS) {
      return (
        <div className={showSearchStyles} key="iosSearchHero">
          <SearchInput
            location={segmentLocation}
            isHeroSearch
            inputClass={searchInputClass}
            inputWrapperClass={styles.inputContainer}
            placeholder={DEFAULT_TEXTS.PLACEHOLDER.heroSearchText}
            activeSearch={isActive}
            autoFocus={isActive}
          />
        </div>
      );
    }
    return isActive ? (
      <SearchInput
        location={segmentLocation}
        isHeroSearch
        inputClass={styles.searchInput}
        inputWrapperClass={styles.inputContainer}
        placeholder={DEFAULT_TEXTS.PLACEHOLDER.heroSearchText}
        activeSearch={isActive}
        autoFocus
        iconName=""
      />
    ) : (
      <p className="p0 n300 paddingLeft-small">{DEFAULT_TEXTS.PLACEHOLDER.heroSearchText}</p>
    );
  };
  const searchStyles = isIOS ? cn(styles.searchbar, styles.searchbarIos) : styles.searchbar;

  useOutsideClick(wrapperRef, handleClose);

  return (
    <section key="heroWrapper" id={SEARCH_WRAPPER_ID} ref={wrapperRef} className={styles.searchWrapper}>
      <Icon name={SEARCH_ICON} className={styles.searchIcon} />
      <div onClick={handleClick} className={searchStyles}>
        {renderSearchComponent()}
      </div>
      {isActive && <Icon name="v2-close-icon" className={styles.closeIcon} onClick={handleClose} />}
    </section>
  );
};

export default HeroSearchbar;
